/* FRYNEST_PRODUCT_GALLERY_20260901 */
(() => {
  const clampIndex = (value, length) => ((value % length) + length) % length;

  class FryNestProductGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-fnx-track]');
      this.slides = Array.from(root.querySelectorAll('[data-fnx-slide]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-fnx-thumb]'));
      this.counter = root.querySelector('[data-fnx-counter]');
      this.lightbox = root.querySelector('[data-fnx-lightbox]');
      this.lightboxImage = root.querySelector('[data-fnx-lightbox-image]');
      this.lightboxCounter = root.querySelector('[data-fnx-lightbox-counter]');
      this.index = 0;
      this.scrollFrame = 0;
      this.programmaticTarget = null;
      this.programmaticTimer = 0;
    }

    init() {
      if (!this.track || !this.slides.length || this.root.dataset.fnxGalleryReady === 'true') return;
      this.root.dataset.fnxGalleryReady = 'true';
      this.slides.forEach((slide) => {
        const zoom = slide.querySelector('[data-fnx-zoom]');
        const image = slide.querySelector('img');
        if (image) image.draggable = false;
        if (zoom) zoom.addEventListener('click', () => this.openLightbox(Number(slide.dataset.index || 0)));
      });
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index || 0)));
      });
      this.root.querySelector('[data-fnx-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
      this.root.querySelector('[data-fnx-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
      this.track.addEventListener('scroll', () => this.queueScrollUpdate(), { passive: true });
      this.track.addEventListener('keydown', (event) => this.onKeydown(event));
      this.lightbox?.querySelector('[data-fnx-lightbox-close]')?.addEventListener('click', () => this.closeLightbox());
      this.lightbox?.querySelector('[data-fnx-lightbox-prev]')?.addEventListener('click', () => this.openLightbox(this.index - 1));
      this.lightbox?.querySelector('[data-fnx-lightbox-next]')?.addEventListener('click', () => this.openLightbox(this.index + 1));
      this.lightbox?.addEventListener('click', (event) => {
        if (event.target === this.lightbox) this.closeLightbox();
      });
      this.lightbox?.addEventListener('close', () => document.documentElement.classList.remove('fnx-lightbox-open'));
      this.setIndex(0, false);
    }

    goTo(nextIndex, smooth = true) {
      const index = clampIndex(nextIndex, this.slides.length);
      clearTimeout(this.programmaticTimer);
      this.programmaticTarget = index;
      this.track.scrollTo({ left: this.slides[index].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      this.setIndex(index, smooth);
      this.programmaticTimer = window.setTimeout(() => {
        this.programmaticTarget = null;
        this.syncToScroll();
      }, 650);
    }

    setIndex(nextIndex, moveThumb = true) {
      this.index = clampIndex(nextIndex, this.slides.length);
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.thumbs.forEach((thumb, index) => {
        const active = index === this.index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', String(active));
      });
      const label = `${this.index + 1} / ${this.slides.length}`;
      if (this.counter) this.counter.textContent = label;
      if (this.lightboxCounter) this.lightboxCounter.textContent = label;
      if (moveThumb && this.thumbs[this.index]) {
        const thumb = this.thumbs[this.index];
        const scroller = thumb.parentElement;
        if (scroller) {
          scroller.scrollTo({
            left: Math.max(0, thumb.offsetLeft - (scroller.clientWidth - thumb.clientWidth) / 2),
            behavior: 'smooth',
          });
        }
      }
    }

    queueScrollUpdate() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        if (this.programmaticTarget !== null) {
          const target = this.slides[this.programmaticTarget];
          if (target && Math.abs(target.offsetLeft - this.track.scrollLeft) <= 2) {
            clearTimeout(this.programmaticTimer);
            this.programmaticTarget = null;
          } else {
            return;
          }
        }
        this.syncToScroll();
      });
    }

    syncToScroll() {
      let closest = 0;
      let distance = Number.POSITIVE_INFINITY;
      this.slides.forEach((slide, index) => {
        const candidate = Math.abs(slide.offsetLeft - this.track.scrollLeft);
        if (candidate < distance) {
          distance = candidate;
          closest = index;
        }
      });
      if (closest !== this.index) this.setIndex(closest);
    }

    onKeydown(event) {
      const actions = {
        ArrowLeft: () => this.goTo(this.index - 1),
        ArrowRight: () => this.goTo(this.index + 1),
        Home: () => this.goTo(0),
        End: () => this.goTo(this.slides.length - 1),
      };
      if (actions[event.key]) {
        event.preventDefault();
        actions[event.key]();
      }
    }

    openLightbox(nextIndex) {
      if (!this.lightbox || !this.lightboxImage) return;
      this.setIndex(clampIndex(nextIndex, this.slides.length));
      const zoom = this.slides[this.index].querySelector('[data-fnx-zoom]');
      const image = this.slides[this.index].querySelector('img');
      this.lightboxImage.src = zoom?.dataset.fullSrc || image?.currentSrc || image?.src || '';
      this.lightboxImage.alt = zoom?.dataset.fnxAlt || image?.alt || '';
      if (!this.lightbox.open) this.lightbox.showModal();
      document.documentElement.classList.add('fnx-lightbox-open');
    }

    closeLightbox() {
      if (this.lightbox?.open) this.lightbox.close();
    }
  }

  const mount = (scope = document) => {
    scope.querySelectorAll('[data-fnx-gallery]').forEach((root) => new FryNestProductGallery(root).init());
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  window.FryNestProductGalleryVersion = '2026-09-01';
})();
